import { AimOutlined } from '@ant-design/icons';
import { modal } from '@repo/ui';
import { AutomationExecutionNodeStatusEnum, AutomationStatus } from '@repo/utils';
import { CaseUtil } from '@vectord/utils';
import { type NodeProps } from '@xyflow/react';
import { NodeType } from 'automations/data/nodeTypes';
import useTriggerNodeConfig from 'automations/hooks/triggerNodes/useTriggerNodeConfig';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { useConfig } from 'automations/hooks/useConfig';
import { automationStatusSelector } from 'automations/selectors/automationConfig';
import { childrenCountSelector } from 'automations/selectors/reactFlow';
import { addNodeWithPosition } from 'automations/store/reactFlowSlice';
import { type TriggerNodeType } from 'automations/types/nodeTypes';
import { deleteNode } from 'automations/utils/deleteNode';
import { generateNewNodePayload } from 'automations/utils/node';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import NodeUi from '../NodeUi';
import styles from './TriggerNode.module.scss';

export default function TriggerNode(node: NodeProps<TriggerNodeType>) {
  const { id, data } = node;
  const dispatch = useAutomationDispatch();
  const { triggers } = useConfig();
  const { setMenuOpen } = useCommonNodeHandles(node);

  const childrenCount = useSelector(childrenCountSelector(id));

  const automationStatus = useSelector(automationStatusSelector);

  const {
    icon: Icon,
    handleEdit,
    handleCopy,
    AdditionalUI,
    hasErrors,
    errorMessages,
  } = useTriggerNodeConfig(node);

  const handleAddNode = useCallback(() => {
    const newNode = generateNewNodePayload({
      nodeType: NodeType.newBlock,
      data: {
        // while adding a new node, mindfully set the execution order
        executionOrder: childrenCount + 1,
      },
    });
    dispatch(addNodeWithPosition({
      newNode,
      sourceNodeId: id,
    }));
  }, [childrenCount, dispatch, id]);

  const triggerContent = useMemo(() => triggers?.find(
    (t) => t.name === data.nodeSubType,
  ), [data.nodeSubType, triggers]);

  const handleDeleteNode = useCallback(() => {
    modal.confirm({
      title: 'Are you sure you want to delete this node?',
      content: 'NOTE: This action cannot be undone, All child nodes of this node will also be deleted.',
      centered: true,
      width: 600,
      onOk: () => dispatch(deleteNode(id)),
    });
  }, [dispatch, id]);

  const targetExecStatus = node?.data?.executionStatus;

  const backgroundStatus = useMemo(() => {
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.SUCCESS) return 'success';
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.FAILED) return 'failed';
    return undefined;
  }, [targetExecStatus]);

  const canDeleteNode = automationStatus === AutomationStatus.draft;

  return (
    <NodeUi
      nodeId={id}
      nodeTitle={CaseUtil.titleCase(data.nodeSubType)}
      nodeSubtitle={triggerContent?.desc ?? ''}
      indicator={{
        indicatorIcon: <AimOutlined />,
        indicatorContent: 'Trigger',
      }}
      icon={{
        iconContent: <Icon className={styles.content} />,
        iconBgClassName: styles.iconBg,
      }}
      targetHandle={{
        onClick: handleAddNode,
      }}
      hasErrors={hasErrors}
      errorMessages={errorMessages}
      contextMenu={{
        onEdit: handleEdit ?? (() => setMenuOpen(true)),
        onCopy: handleCopy,
        onDelete: canDeleteNode ? handleDeleteNode : undefined,
      }}
      AdditionalUI={AdditionalUI}
      debugContent={data}
      successExecutionCounts={data?.successExecutionCounts}
      backgroundStatus={backgroundStatus}
    />
  );
}
